import React, {useState, useEffect, useRef} from 'react';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase/firebase';
import { useAuth } from '../contexts/authContext';
import {
    Form,
    Input,
    Button,
    Select,
    Checkbox,
    Spin,
    Alert,
    Typography,
    message,
    Card,
    Tooltip,
    Collapse
} from 'antd';
import {Link, useLocation, useNavigate} from "react-router-dom";
import ReactPlayer from "react-player";
import {setUserId} from "../config/amplitude/amplitude";

const { Option } = Select;
const { Title } = Typography;
const { Panel } = Collapse;

const DouConfigPage = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [highlightVideo2, setHighlightVideo2] = useState(false);
    const location = useLocation();

    const video2Ref = useRef(null);

    useEffect(() => {
        document.title = 'Dou Config - CV Postman App';
        localStorage.setItem('lastOpenedPage', '/job-config/dou-config');
        setUserId(currentUser.email);
        // Check query parameters to see if the second video should be highlighted
        const params = new URLSearchParams(location.search);
        if (params.get("highlight") === "video2") {
            setHighlightVideo2(true);
            // Scroll to the second video after the component is mounted
            setTimeout(() => {
                video2Ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 100); // Delay to ensure the component is fully rendered
        }
    }, [location]);

    const experienceOptions = ["1-3", "3-5", "5plus"];
    const locationOptions = [
        "Київ", "Львів", "Дніпро", "Вінниця", "Івано-Франківськ", "Тернопіль",
        "Ужгород", "Харків", "Одеса", "Хмельницький", "Чернівці", "Житомир",
        "Кременчук", "Луцьк", "Полтава", "Черкаси", "віддалена робота", "за кордоном"
    ];
    const specializationOptions = [
        ".NET", "Account Manager", "AI/ML", "Analyst", "Android", "Animator", "Architect", "Artist", "Assistant",
        "Big Data", "Blockchain", "C++", "C-level", "Copywriter", "Data Engineer", "Data Science", "DBA", "Design",
        "DevOps", "Embedded", "Engineering Manager", "Erlang", "ERP/CRM", "Finance", "Flutter", "Front End", "Golang",
        "Hardware", "HR", "iOS/macOS", "Java", "Legal", "Marketing", "Node.js", "Office Manager", "Other", "PHP",
        "Product Manager", "Project Manager", "Python", "QA", "React Native", "Ruby", "Rust", "Sales", "Salesforce",
        "SAP", "Scala", "Scrum Master", "Security", "SEO", "Support", "SysAdmin", "Technical Writer", "Unity",
        "Unreal Engine"
    ];

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const userEmail = currentUser.email;
                const docRef = doc(db, `users/${userEmail}/dou.config/1`);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    const data = docSnap.data();
                    form.setFieldsValue({
                        douEmail: data['dou.email'] || '',
                        douPassword: data['dou.password'] || '',
                        douWebsite: data['dou.website'] || 'https://dou.ua',
                        jobExperienceRange: data['job.experience.range'] || [],
                        locations: data['job.location'] || [],
                        specialization: data['job.specialization'] || '',
                        excludeCompanies: data['companies.exclude'] || '',
                        excludeWords: data['words.exclude'] || '',
                        includeWords: data['words.include'] || '',
                    });
                } else {
                    console.log("No such document!");
                }
            } catch (err) {
                console.error("Error saving data:", err);
                message.error(err.message);
            } finally {
                setLoading(false);
            }
        };

        if (currentUser) {
            fetchData();
        }
    }, [currentUser, form]);

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            const userEmail = currentUser.email;
            const docRef = doc(db, `users/${userEmail}/dou.config/1`);
            await setDoc(docRef, {
                'dou.email': values.douEmail,
                'dou.password': values.douPassword,
                'dou.website': values.douWebsite,
                'job.experience.range': values.jobExperienceRange,
                'job.location': values.locations,
                'job.specialization': values.specialization,
                'companies.exclude': values.excludeCompanies || "",
                'words.exclude': values.excludeWords || "",
                'words.include': values.includeWords || "",
            });
            message.success("Saved successfully!");
            //setTimeout(() => navigate('/job-config/token-config'), 1300);
        } catch (err) {
            console.error("Error saving data:", err);
            message.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Spin spinning={loading}>
            <div style={{maxWidth: 600, margin: '0 auto', padding: '20px'}}>
                <Title level={3}>DOU Configuration</Title>
                <Form
                    form={form}
                    onFinish={handleSubmit}
                    layout="vertical"
                    initialValues={{
                        douWebsite: 'https://dou.ua',
                    }}
                >
                    <Card>
                        <Form.Item label="Website" name="douWebsite" style={{display: 'none'}}
                                   rules={[{required: true, message: 'Please enter the website URL!'}]}>
                            <Input placeholder="https://dou.ua"/>
                        </Form.Item>
                        <Form.Item label="Specialization" name="specialization"
                                   rules={[{required: true, message: 'Please enter job specialization!'}]}>
                            <Select allowClear placeholder="Select specialization">
                                {specializationOptions.map((specialization) => (
                                    <Option key={specialization} value={specialization}>
                                        {specialization}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item label="Job Experience Range" name="jobExperienceRange"
                                   rules={[{required: true, message: 'Please enter job experience range!'}]}>
                            <Checkbox.Group options={experienceOptions}/>
                        </Form.Item>
                        <Form.Item
                            label="Locations"
                            name="locations"
                            rules={[{required: true, message: 'Please enter job locations!'}]}
                        >
                            <Select
                                mode="multiple"
                                allowClear
                                placeholder="Select up to 2 locations"
                                value={form.getFieldValue("locations")}
                                onChange={(value) => {
                                    if (value.length > 2) {
                                        message.warning("You can only select up to 2 locations.");
                                        // Trim the selection back to first 2 items
                                        const trimmed = value.slice(0, 2);
                                        form.setFieldsValue({locations: trimmed});
                                        return;
                                    }
                                    form.setFieldsValue({locations: value});
                                }}
                            >
                                {locationOptions.map((location) => (
                                    <Option key={location} value={location}>
                                        {location}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Card>
                    <Card>
                        <Collapse defaultActiveKey={[]} ghost>
                            <Panel header="Additional filters (optional):" key="1">
                                <Tooltip title="Exclude vacancy if company name CONTAINS ANY of these words (comma seperated list)">
                                    <Form.Item label="⛔️Exclude companies" name="excludeCompanies"
                                               rules={[{required: false, message: 'Please enter companies to avoid!'}]}>
                                        <Input placeholder="SoftServe, Epam, Global Logic"/>
                                    </Form.Item>
                                </Tooltip>
                                <Tooltip title="Exclude vacancy if ANY of these words appears in job title or job description (comma seperated list)">
                                    <Form.Item label="⛔Exclude words" name="excludeWords"
                                               rules={[{
                                                   required: false,
                                                   message: 'Please enter words to avoid in the vacancy!'
                                               }]}>
                                        <Input placeholder="Angular, VueJs, iGaming, Crypto"/>
                                    </Form.Item>
                                </Tooltip>
                                <Tooltip title="Include vacancy if ANY of these words exist in the job title or job description (comma seperated list)">
                                    <Form.Item label="✅Include words" name="includeWords"
                                               rules={[{
                                                   required: false,
                                                   message: 'Please enter words to include in the vacancy!'
                                               }]}>
                                        <Input placeholder="React, ReactJs, React.Js"/>
                                    </Form.Item>
                                </Tooltip>
                            </Panel>
                        </Collapse>
                    </Card>
                    <br/>
                    <Card title="DOU credentials:">
                        <Form.Item label="Email" name="douEmail"
                                   rules={[{required: true, message: 'Please enter your email!'}]}>
                            <Input placeholder="Enter your DOU email"/>
                        </Form.Item>
                        <Form.Item label="Password" name="douPassword"
                                   rules={[{required: true, message: 'Please enter your password!'}]}>
                            <Input.Password placeholder="Enter your DOU password"/>
                        </Form.Item>
                        <div style={{textAlign: 'right', marginTop: '10px'}}>
                            <Link to="/job-config/dou-config?highlight=video2"
                                  style={{fontSize: '12px', color: '#1890ff'}}>
                                How to get dou password (video)?
                            </Link>
                        </div>
                        <Alert
                            style={{marginTop: 16}}
                            message="Automation will Not work if you do not provide a valid password (!) from dou.ua"
                            type="success"
                        />

                    </Card>
                    <br/><br/>
                    <Form.Item style={{ textAlign: 'center' }}>
                        <Button type="primary" htmlType="submit">
                            Save Configuration
                        </Button>
                    </Form.Item>
                </Form>
                <br/><br/><br/><br/>
                <div
                    ref={video2Ref}
                    style={{marginBottom: "20px", border: highlightVideo2 ? "2px solid #1890ff" : "none"}}
                >
                    <ReactPlayer url="https://youtu.be/hNYPH8Ho8Kk" width="100%" controls/>
                </div>
            </div>
        </Spin>
    );
};

export default DouConfigPage;
